import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { ESTADOS_EMPRESA, ESTADOS_UAF, fmtFecha } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { PageHeader } from "../../components/PageHeader";
import { StatusBadge } from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const TRANSICIONES = {
  pendiente_verificacion: ["activa", "rechazada"],
  activa: ["suspendida"],
  suspendida: ["activa", "rechazada"],
  rechazada: ["pendiente_verificacion"],
};

const ActionDialog = ({ action, onClose, onDone }) => {
  const [estado, setEstado] = useState("");
  const [motivo, setMotivo] = useState("");
  const { refresh } = useAuth();
  const navigate = useNavigate();
  if (!action) return null;
  const { type, c } = action;
  const submit = async () => {
    try {
      if (type === "estado") {
        await api.post(`/admin/companies/${c.id}/estado`, { estado, motivo });
        toast.success("Estado actualizado");
        onDone();
      } else {
        await api.post("/admin/support-access", { company_id: c.id, motivo });
        await refresh();
        navigate("/panel");
      }
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={`${type}-dialog`}>
        <DialogHeader>
          <DialogTitle>{type === "estado" ? "Cambiar estado" : "Acceso de soporte"} · {c.nombre_fantasia}</DialogTitle>
          <DialogDescription>
            {type === "estado" ? `Estado actual: ${ESTADOS_EMPRESA[c.estado]}. El motivo es obligatorio y queda en la bitácora.`
              : "Acceso de solo lectura por 1 hora. El motivo es obligatorio y queda en la bitácora de la empresa."}
          </DialogDescription>
        </DialogHeader>
        {type === "estado" && (
          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger data-testid="estado-select"><SelectValue placeholder="Nuevo estado" /></SelectTrigger>
            <SelectContent>{TRANSICIONES[c.estado].map((e) => <SelectItem key={e} value={e}>{ESTADOS_EMPRESA[e]}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <div className="space-y-1.5">
          <Label>Motivo (mínimo 10 caracteres)</Label>
          <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="motivo-input" />
        </div>
        <DialogFooter>
          <Button disabled={motivo.trim().length < 10 || (type === "estado" && !estado)} onClick={submit} className="bg-slate-900 hover:bg-slate-800" data-testid={`${type}-confirm-button`}>Confirmar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DetailDialog = ({ c, onClose }) => (
  <Dialog open={!!c} onOpenChange={(v) => !v && onClose()}>
    {c && (
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="company-detail-dialog">
        <DialogHeader><DialogTitle>{c.razon_social}</DialogTitle><DialogDescription>{c.rut} · {c.giro} · {c.comuna}</DialogDescription></DialogHeader>
        <div className="text-sm space-y-3">
          <p><b>Representante legal:</b> {c.representante_legal.nombre} · {c.representante_legal.run} · {c.representante_legal.correo}</p>
          <p><b>Oficial de cumplimiento:</b> {c.oficial_cumplimiento.nombre} · {c.oficial_cumplimiento.run} · {c.oficial_cumplimiento.cargo} · {c.oficial_cumplimiento.correo}</p>
          <p><b>UAF:</b> {ESTADOS_UAF[c.estado_uaf]}</p>
          <p className="font-semibold pt-2">Aceptaciones legales</p>
          {c.aceptaciones.map((a) => <p key={a.documento} className="text-xs font-mono">{a.documento} v{a.version} · {fmtFecha(a.fecha)} · IP {a.ip} · {a.correo}</p>)}
          <p className="font-semibold pt-2">Historial de estados</p>
          {c.estado_historial.map((h, i) => <p key={i} className="text-xs">{fmtFecha(h.fecha)} · <b>{ESTADOS_EMPRESA[h.estado]}</b> · {h.motivo}</p>)}
        </div>
      </DialogContent>
    )}
  </Dialog>
);

export default function Empresas() {
  const [items, setItems] = useState([]);
  const [action, setAction] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => api.get("/admin/companies").then((r) => setItems(r.data.items)), []);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-8">
      <PageHeader overline="Superadmin" title="Empresas" />
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <Table data-testid="companies-table">
          <TableHeader><TableRow>{["Empresa", "RUT", "Estado", "UAF", "Registro", "Acciones"].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`company-row-${c.slug}`}>
                <TableCell><p className="font-medium">{c.nombre_fantasia}</p><p className="text-xs text-slate-500">{c.razon_social}</p></TableCell>
                <TableCell className="font-mono text-xs">{c.rut}</TableCell>
                <TableCell><StatusBadge value={c.estado} /></TableCell>
                <TableCell className="text-xs">{ESTADOS_UAF[c.estado_uaf]}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{fmtFecha(c.created_at)}</TableCell>
                <TableCell className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => setDetail(c)} data-testid={`company-detail-${c.slug}`}>Ver</Button>
                  <Button size="sm" variant="outline" onClick={() => setAction({ type: "estado", c })} data-testid={`company-estado-${c.slug}`}>Cambiar estado</Button>
                  <Button size="sm" variant="ghost" onClick={() => setAction({ type: "soporte", c })} data-testid={`company-soporte-${c.slug}`}>Soporte</Button>
                </TableCell>
              </TableRow>
            ))}
            {!items.length && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-10">Aún no hay empresas registradas</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <ActionDialog key={action ? `${action.type}-${action.c.id}` : "none"} action={action} onClose={() => setAction(null)} onDone={load} />
      <DetailDialog c={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
